
import React, { useState } from 'react';
import { Users, ClipboardCheck, Bell, PlusCircle, Calendar, Printer } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTimetableData } from '../../hooks/useTimetableData';
import TimetableButton from './TimetableButton';
import TimetablePageSelectionDialog from './TimetablePageSelectionDialog';
import TimetableFormatDialog from './TimetableFormatDialog';
import { printTimetableDataPaginatedAsJPG, printTimetableDataPaginatedAsPDF } from '@/utils/printing/timetablePaginatedPrinter';
import { toast } from 'sonner';

const RECORDS_PER_PAGE = 20; // Updated to 20 for timetables as requested

const TimetableHeader = () => {
  const navigate = useNavigate();
  const { hasClassesToday, timetableData } = useTimetableData();
  const [isPrinting, setIsPrinting] = useState(false);
  const [isPageDialogOpen, setIsPageDialogOpen] = useState(false);
  const [isFormatDialogOpen, setIsFormatDialogOpen] = useState(false);
  const [selectedPages, setSelectedPages] = useState<{ start: number; end: number } | null>(null);

  const handlePrintTimetable = () => {
    if (!timetableData || timetableData.length === 0) {
      toast.error("No timetable data available to download");
      return;
    }

    // Calculate total classes
    const totalClasses = timetableData.reduce((total, teacher) => total + (teacher.classes?.length || 0), 0);
    const totalPages = Math.ceil(totalClasses / RECORDS_PER_PAGE);

    if (totalPages === 1) {
      setSelectedPages({ start: 1, end: 1 });
      setIsFormatDialogOpen(true);
    } else {
      setIsPageDialogOpen(true);
    }
  };

  const handlePageSelection = (startPage: number, endPage: number) => {
    setSelectedPages({ start: startPage, end: endPage });
    setIsPageDialogOpen(false);
    setIsFormatDialogOpen(true);
  };

  const handleFormatDownload = async (downloadFormat: 'jpg' | 'pdf') => {
    if (isPrinting) return;
    
    setIsPrinting(true);
    
    // Add loading toast notification
    const loadingToastId = toast.loading(`Generating Timetable Report ${downloadFormat.toUpperCase()}...`);
    
    try {
      const totalClasses = timetableData?.reduce((total, teacher) => total + (teacher.classes?.length || 0), 0) || 0;
      const startPage = selectedPages?.start || 1;
      const endPage = selectedPages?.end || Math.ceil(totalClasses / RECORDS_PER_PAGE);
      
      if (downloadFormat === 'pdf') {
        await printTimetableDataPaginatedAsPDF(timetableData || [], "Timetable Report", startPage, endPage);
      } else {
        await printTimetableDataPaginatedAsJPG(timetableData || [], "Timetable Report", startPage, endPage);
      }
      
      // Dismiss the loading toast - success toast will be shown by the printing functions
      toast.dismiss(loadingToastId);
      
    } catch (error) {
      console.error('Error downloading timetable:', error);
      toast.error("Failed to download timetable", { id: loadingToastId });
    } finally {
      setIsPrinting(false);
      setIsFormatDialogOpen(false);
      setSelectedPages(null);
    }
  };

  return (
    <>
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center space-y-4 md:space-y-0">
        <div>
          <h1 className="text-2xl font-bold text-primary flex items-center">
            <Calendar className="mr-2 h-6 w-6" />
            Teacher Timetable
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage class schedules and teacher availability
          </p>
        </div>
        <div className="flex flex-wrap gap-2 w-full md:w-auto justify-start md:justify-end">
          <TimetableButton
            icon={Users}
            onClick={() => navigate('/teachers')}
          >
            Teachers
          </TimetableButton>
          
          <TimetableButton
            icon={ClipboardCheck}
            onClick={() => navigate('/attendance')}
          >
            Attendance
          </TimetableButton>
          
          <TimetableButton
            icon={Bell}
            onClick={() => navigate('/timetable/alerts')}
            notification={hasClassesToday()}
          >
            Alerts
          </TimetableButton>
          
          <TimetableButton
            icon={PlusCircle}
            onClick={() => navigate('/timetable/new')}
            variant="default"
          >
            New Schedule
          </TimetableButton>
          
          <TimetableButton
            icon={Printer}
            onClick={handlePrintTimetable}
            variant="outline"
            disabled={isPrinting}
          >
            {isPrinting ? 'Processing...' : 'Print Timetable'}
          </TimetableButton>
        </div>
      </div>

      <TimetablePageSelectionDialog
        isOpen={isPageDialogOpen}
        onClose={() => setIsPageDialogOpen(false)}
        totalRecords={timetableData?.reduce((total, teacher) => total + (teacher.classes?.length || 0), 0) || 0}
        recordsPerPage={RECORDS_PER_PAGE}
        onDownload={handlePageSelection}
        reportTitle="Timetable Report"
      />

      <TimetableFormatDialog
        isOpen={isFormatDialogOpen}
        onClose={() => {
          setIsFormatDialogOpen(false);
          setSelectedPages(null);
        }}
        onDownload={handleFormatDownload}
        totalRecords={timetableData?.reduce((total, teacher) => total + (teacher.classes?.length || 0), 0) || 0}
        reportTitle="Timetable Report"
      />
    </>
  );
};

export default TimetableHeader;
